/** Wheel delta → px для scrollTop/scrollLeft. */
export function wheelDeltaPixels(ev: WheelEvent, el: HTMLElement): { dx: number; dy: number } {
  if (ev.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return { dx: ev.deltaX * 16, dy: ev.deltaY * 16 };
  }
  if (ev.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    const page = el.clientHeight || window.innerHeight;
    return { dx: ev.deltaX * page, dy: ev.deltaY * page };
  }
  return { dx: ev.deltaX, dy: ev.deltaY };
}

function canScrollY(el: HTMLElement, dy: number): boolean {
  if (!dy) return false;
  const style = getComputedStyle(el);
  if (!/(auto|scroll|overlay)/.test(style.overflowY)) return false;
  if (el.scrollHeight <= el.clientHeight + 1) return false;
  if (dy > 0) return el.scrollTop + el.clientHeight < el.scrollHeight - 0.5;
  return el.scrollTop > 0.5;
}

function canScrollX(el: HTMLElement, dx: number): boolean {
  if (!dx) return false;
  const style = getComputedStyle(el);
  if (!/(auto|scroll|overlay)/.test(style.overflowX)) return false;
  if (el.scrollWidth <= el.clientWidth + 1) return false;
  if (dx > 0) return el.scrollLeft + el.clientWidth < el.scrollWidth - 0.5;
  return el.scrollLeft > 0.5;
}

/**
 * Ближайший предок с overflow, который может прокрутиться в направлении wheel.
 * На границе (верх/низ) — null, жест уходит на pan/zoom доски.
 */
export function findScrollableWheelTarget(
  target: EventTarget | null,
  ev: WheelEvent,
  root?: Element | null
): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  const probe = target instanceof HTMLElement ? target : document.body;
  const { dx, dy } = wheelDeltaPixels(ev, probe);

  let el: Element | null = target;
  while (el && el !== root) {
    if (el instanceof HTMLElement) {
      if (canScrollY(el, dy) || canScrollX(el, dx)) return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Начали прокручивать список внутри виджета — весь жест остаётся в нём,
 * даже когда список упёрся в край. Иначе хвост инерции тачпада докручивает
 * доску и уводит камеру от виджета.
 */
export const WHEEL_SCROLL_LATCH_MS = 320;

export type WheelScrollLatch = { el: HTMLElement | null; ts: number };

/**
 * Куда отдать колесо: во внутренний список или камере доски (null).
 * Пока жест продолжается, держимся за список, который крутили последним.
 */
export function resolveWheelScrollTarget(
  ev: WheelEvent,
  root: Element | null,
  latch: WheelScrollLatch,
  now: number
): HTMLElement | null {
  const scrollEl = findScrollableWheelTarget(ev.target, ev, root);
  if (scrollEl) return scrollEl;
  const held = latch.el;
  if (!held || !held.isConnected) return null;
  if (now - latch.ts >= WHEEL_SCROLL_LATCH_MS) return null;
  const target = ev.target;
  if (held === target || (target instanceof Node && held.contains(target))) return held;
  return null;
}

export function applyWheelToScrollable(el: HTMLElement, ev: WheelEvent): void {
  const { dx, dy } = wheelDeltaPixels(ev, el);
  if (dy) el.scrollTop += dy;
  if (dx) el.scrollLeft += dx;
}

/** Прокрутка конкретного контейнера (полоска слайдов PDF). */
export function scrollElementFromWheel(el: HTMLElement, ev: WheelEvent): boolean {
  if (ev.ctrlKey || ev.metaKey) return false;
  const { dx, dy } = wheelDeltaPixels(ev, el);
  if (!canScrollY(el, dy) && !canScrollX(el, dx)) return false;
  applyWheelToScrollable(el, ev);
  return true;
}
